import React, { Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';

import { lazyPage, PageSkeleton } from './routes/lazyPages';
import './index.css';

// Every page is its own chunk: a user downloads only the pages they open.
// lazyPage() also records the loader so the sidebar can prefetch on hover.
const Login                  = lazyPage('./pages/Login', () => import('./pages/Login'));
const Apply                  = lazyPage('./pages/Apply', () => import('./pages/Apply'));
const SalesDashboard         = lazyPage('./pages/sales/SalesDashboard', () => import('./pages/sales/SalesDashboard'));
const LeadsList              = lazyPage('./pages/sales/LeadsList', () => import('./pages/sales/LeadsList'));
const LeadDetail             = lazyPage('./pages/sales/LeadDetail', () => import('./pages/sales/LeadDetail'));
const PipelineKanban         = lazyPage('./pages/sales/PipelineKanban', () => import('./pages/sales/PipelineKanban'));
const CallCadence            = lazyPage('./pages/sales/CallCadence', () => import('./pages/sales/CallCadence'));
const HiringIntel            = lazyPage('./pages/sales/HiringIntel', () => import('./pages/sales/HiringIntel'));
const OutreachHub            = lazyPage('./pages/hubs/OutreachHub', () => import('./pages/hubs/OutreachHub'));
const ReportsHub             = lazyPage('./pages/hubs/ReportsHub', () => import('./pages/hubs/ReportsHub'));
const DataHub                = lazyPage('./pages/hubs/DataHub', () => import('./pages/hubs/DataHub'));
const Tasks                  = lazyPage('./pages/Tasks', () => import('./pages/Tasks'));
const RecruitmentDashboard   = lazyPage('./pages/recruitment/RecruitmentDashboard', () => import('./pages/recruitment/RecruitmentDashboard'));
const JobsList               = lazyPage('./pages/recruitment/JobsList', () => import('./pages/recruitment/JobsList'));
const CandidatesList         = lazyPage('./pages/recruitment/CandidatesList', () => import('./pages/recruitment/CandidatesList'));
const CandidateDetail        = lazyPage('./pages/recruitment/CandidateDetail', () => import('./pages/recruitment/CandidateDetail'));
const Pipeline               = lazyPage('./pages/recruitment/Pipeline', () => import('./pages/recruitment/Pipeline'));
const Interviews             = lazyPage('./pages/recruitment/Interviews', () => import('./pages/recruitment/Interviews'));
const ResumeHub              = lazyPage('./pages/recruitment/ResumeHub', () => import('./pages/recruitment/ResumeHub'));
const Timesheet              = lazyPage('./pages/timesheet/Timesheet', () => import('./pages/timesheet/Timesheet'));
const TimesheetApprovals     = lazyPage('./pages/timesheet/TimesheetApprovals', () => import('./pages/timesheet/TimesheetApprovals'));
const Settings               = lazyPage('./pages/Settings', () => import('./pages/Settings'));
const CEODashboard           = lazyPage('./pages/CEODashboard', () => import('./pages/CEODashboard'));
const AuditLog               = lazyPage('./pages/AuditLog', () => import('./pages/AuditLog'));
const ExpenseTracker         = lazyPage('./pages/expenses/ExpenseTracker', () => import('./pages/expenses/ExpenseTracker'));

// ── Helpers ──────────────────────────────────────────────────

// Wraps a page with auth + optional module + optional permission guard + Layout
const Page = ({ children, module: reqModule, mod, permission }) => (
  <ProtectedRoute requiresModule={reqModule || mod} requiresPermission={permission}>
    <Layout><Suspense fallback={<PageSkeleton />}>{children}</Suspense></Layout>
  </ProtectedRoute>
);

// Public pages (no Layout) get a minimal fallback.
const Public = ({ children }) => (
  <Suspense fallback={<PageSkeleton bare />}>{children}</Suspense>
);

// Redirect based on role after login
const DefaultRedirect = () => {
  const { user } = useAuth();
  if (user?.role === 'worker') return <Navigate to="/timesheet" replace />;
  return <Navigate to="/sales" replace />;
};

const TimesheetRoute = () => {
  return (
    <Page module="timesheet">
      <Timesheet />
    </Page>
  );
};

// ── Routes ───────────────────────────────────────────────────
function AppRoutes() {
  return (
    <Routes>
      {/* ── Public routes — no auth required ── */}
      <Route path="/apply" element={<Public><Apply /></Public>} />
      <Route path="/login" element={<Public><Login /></Public>} />

      {/* ── Sales — admin, sales, viewer only ── */}
      <Route path="/sales"              element={<Page module="sales"><SalesDashboard /></Page>} />
      <Route path="/sales/leads"        element={<Page module="sales"><LeadsList /></Page>} />
      <Route path="/sales/leads/:id"    element={<Page module="sales"><LeadDetail /></Page>} />
      <Route path="/sales/tasks"        element={<Page module="sales"><Tasks /></Page>} />
      <Route path="/sales/reminders"    element={<Navigate to="/sales/tasks?tab=reminders" replace />} />
      <Route path="/sales/pipeline"       element={<Page module="sales"><PipelineKanban /></Page>} />
      <Route path="/sales/call-cadence"   element={<Page module="sales"><CallCadence /></Page>} />
      <Route path="/sales/hiring-intel"   element={<Page module="sales"><HiringIntel /></Page>} />

      {/* ── Merged pages (v2): one sidebar entry each, tabs inside ── */}
      <Route path="/sales/outreach"       element={<Page module="sales"><OutreachHub /></Page>} />
      <Route path="/sales/reports"        element={<Page module="sales"><ReportsHub /></Page>} />
      <Route path="/sales/data"           element={<Page module="sales" permission="canViewImport"><DataHub /></Page>} />

      {/* ── Old URLs keep working (bookmarks, emails, tests) ── */}
      <Route path="/sales/sequences"      element={<Navigate to="/sales/outreach?tab=sequences" replace />} />
      <Route path="/sales/bulk-email"     element={<Navigate to="/sales/outreach?tab=bulk" replace />} />
      <Route path="/sales/tracker"        element={<Navigate to="/sales/reports?tab=tracker" replace />} />
      <Route path="/sales/command-centre" element={<Navigate to="/sales/reports?tab=channels" replace />} />
      <Route path="/sales/activity-log"   element={<Navigate to="/sales/reports?tab=log" replace />} />
      <Route path="/sales/tracker-legacy" element={<Navigate to="/sales/reports?tab=tracker" replace />} />
      <Route path="/sales/import"         element={<Navigate to="/sales/data?tab=import" replace />} />
      <Route path="/sales/enrich"         element={<Navigate to="/sales/data?tab=enrich" replace />} />

      {/* ── Recruitment — admin, sales, viewer only ── */}
      <Route path="/recruitment"                   element={<Page module="recruitment"><RecruitmentDashboard /></Page>} />
      <Route path="/recruitment/jobs"              element={<Page module="recruitment"><JobsList /></Page>} />
      <Route path="/recruitment/jobs/:id"          element={<Page module="recruitment"><JobsList /></Page>} />
      <Route path="/recruitment/candidates"        element={<Page module="recruitment"><CandidatesList /></Page>} />
      <Route path="/recruitment/candidates/:id"    element={<Page module="recruitment"><CandidateDetail /></Page>} />
      <Route path="/recruitment/import-candidates" element={<Navigate to="/recruitment/resume-hub?tab=import" replace />} />
      <Route path="/recruitment/pipeline"          element={<Page module="recruitment"><Pipeline /></Page>} />
      <Route path="/recruitment/interviews"        element={<Page module="recruitment"><Interviews /></Page>} />
      <Route path="/recruitment/tasks"             element={<Page module="recruitment"><Tasks /></Page>} />
      <Route path="/recruitment/resume-hub"        element={<Page module="recruitment"><ResumeHub /></Page>} />
      <Route path="/recruitment/ats-match"         element={<Navigate to="/recruitment/resume-hub?tab=match" replace />} />
      <Route path="/recruitment/ats"               element={<Navigate to="/recruitment/resume-hub?tab=match" replace />} />
      <Route path="/recruitment/ats-score"         element={<Navigate to="/recruitment/resume-hub?tab=score" replace />} />
      <Route path="/recruitment/bulk-upload"       element={<Navigate to="/recruitment/resume-hub?tab=bulk" replace />} />

      {/* ── Timesheet — all roles ── */}
      <Route path="/timesheet"           element={<TimesheetRoute />} />
      {/* Approvals — viewer (CEO) only ── */}
      <Route path="/timesheet/approvals" element={<Page module="timesheet" permission="viewTimesheetApprovals"><TimesheetApprovals /></Page>} />

      {/* ── Admin/CEO pages — permission guarded ── */}
      <Route path="/settings"   element={<Page permission="viewSettings"><Settings /></Page>} />
      <Route path="/ceo"        element={<Page permission="viewCEO"><CEODashboard /></Page>} />
      <Route path="/audit-log"  element={<Page permission="viewCEO"><AuditLog /></Page>} />
      <Route path="/expenses"   element={<Page permission="viewExpenses"><ExpenseTracker /></Page>} />

      {/* ── Default / 404 ── */}
      <Route path="/"  element={<DefaultRedirect />} />
      <Route path="*"  element={<DefaultRedirect />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
